import { getStoredToken } from './auth-client';

// Use empty string for same-origin requests (proxied via Vite proxy)
const API_BASE_URL = '';

interface RequestOptions extends RequestInit {
  params?: Record<string, string>;
  skipAuthRedirect?: boolean;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { params, skipAuthRedirect, ...fetchOptions } = options;

    let url = `${this.baseUrl}${endpoint}`;
    if (params) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }

    // Get Bearer token from localStorage
    const token = getStoredToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...fetchOptions.headers,
    };

    const response = await fetch(url, {
      ...fetchOptions,
      headers,
      // No credentials - using bearer tokens only
    });

    // Handle 401 Unauthorized - redirect to login
    if (response.status === 401 && !skipAuthRedirect) {
      // Only redirect in browser environment
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      throw new Error('Unauthorized');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || error.error || `HTTP ${response.status}`);
    }

    // Handle empty responses
    const text = await response.text();
    if (!text) {
      return {} as T;
    }

    return JSON.parse(text);
  }

  get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  post<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  put<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  patch<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  delete<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'DELETE',
      body: data ? JSON.stringify(data) : undefined,
    });
  }
}

export const api = new ApiClient(API_BASE_URL);

// API endpoints
export const endpoints = {
  // Health
  health: '/health',
  healthDetailed: '/health/detailed',

  // Auth (handled by Better Auth at /api/auth/*)
  // Use authClient from auth-client.ts for authentication
  forgotPassword: '/api/auth/forgot-password',
  resetPassword: '/api/auth/reset-password',

  // User Profile
  profile: '/api/v1/user/profile',
  changePassword: '/api/v1/user/password',
  avatar: '/api/v1/user/avatar',
  deleteAccount: '/api/v1/user/account',

  // User API Keys
  userApiKeys: '/api/v1/user/api-keys',

  // CRM
  contacts: '/api/v1/crm/contacts',
  companies: '/api/v1/crm/companies',
  deals: '/api/v1/crm/deals',

  // Workflows
  workflows: '/api/v1/workflows',

  // Integrations
  connections: '/api/v1/integrations/connections',

  // SSO
  ssoConnections: '/api/v1/sso/connections',

  // Authorization
  authCheck: '/api/v1/authorization/check',

  // Gateway
  gateway: '/api/v1/gateway',

  // Admin - Context
  adminContext: '/api/v1/admin/context',

  // Admin - Users
  adminUsers: '/api/v1/admin/users',
  adminUsersStats: '/api/v1/admin/users/stats',

  // Admin - Organizations
  adminOrganizations: '/api/v1/admin/organizations',

  // Admin - Audit
  adminAudit: '/api/v1/admin/audit',
  adminAuditSummary: '/api/v1/admin/audit/summary',

  // Admin - System
  adminSystemHealth: '/api/v1/admin/system/health',
  adminDbStats: '/api/v1/admin/system/db-stats',
  adminRedisStats: '/api/v1/admin/system/redis-stats',

  // Admin - Data Viewers
  adminPostgresTables: '/api/v1/admin/data/postgres/tables',
  adminPostgresQuery: '/api/v1/admin/data/postgres/query',
  adminRedisKeys: '/api/v1/admin/data/redis/keys',
  adminRedisGet: '/api/v1/admin/data/redis/get',
  adminClickhouseQuery: '/api/v1/admin/data/clickhouse/query',

  // Admin - Policies
  adminCasbinPolicies: '/api/v1/admin/policies/casbin',
  adminCedarPolicies: '/api/v1/admin/policies/cedar',
  adminApiKeys: '/api/v1/admin/api-keys',

  // MCP Tools
  mcpTools: '/api/v1/mcp/tools',
  mcpToolsSearch: '/api/v1/mcp/tools/search',
  mcpToolsPopular: '/api/v1/mcp/tools/popular',
  mcpCategories: '/api/v1/mcp/categories',
  mcpProviders: '/api/v1/mcp/providers',
  mcpTags: '/api/v1/mcp/tags',
  mcpStats: '/api/v1/mcp/stats',
  mcpHealth: '/api/v1/mcp/health',
  mcpSse: '/api/v1/mcp/sse',
};
